import { Checkbox, Col, Form, Input, Radio, Row } from "antd";

import CustomCard from "../../components/CustomCard";
import type { RiskAssessmentQuestionSectionProps } from "./RiskAssessmentForm.shared";
import zuuStyles from "./RiskAssessmentZuuForm.module.css";

const wealthSourceOptions = [
  { label: "工資", value: "A" },
  { label: "儲蓄", value: "B" },
  { label: "投資收入", value: "C" },
  { label: "遺產承繼", value: "D" },
  { label: "公司利潤", value: "E" },
  { label: "其他", value: "F" },
];

const transactionMethodOptions = [
  { label: "支票 / 本票 / 信用咭", value: "A" },
  { label: "入賬到銀行 / 自動櫃員機 / 保險公司", value: "B" },
  { label: "電匯 / 銀行戶口自動轉賬", value: "C" },
  { label: "網上銀行轉賬 / 轉數快", value: "D" },
  { label: "第三者付款", value: "E" },
  { label: "現金", value: "F" },
];

function ThreeLevelRadio({ name, label, disabled }: { name: string; label: string; disabled: boolean }) {
  return (
    <Form.Item name={name} label={label} rules={[{ required: true, message: "請選擇" }]}>
      <Radio.Group disabled={disabled} className={zuuStyles.levelOptions}>
        <Radio value="A">低風險</Radio>
        <Radio value="B">中風險</Radio>
        <Radio value="C">高風險</Radio>
      </Radio.Group>
    </Form.Item>
  );
}

function YesNoRadio({ name, label, disabled }: { name: string; label: string; disabled: boolean }) {
  return (
    <Form.Item name={name} label={label} rules={[{ required: true, message: "請選擇" }]}>
      <Radio.Group disabled={disabled} className={zuuStyles.binaryOptions}>
        <Radio value="A">是</Radio>
        <Radio value="B">否</Radio>
      </Radio.Group>
    </Form.Item>
  );
}

export function RiskAssessmentZuuForm({ form, isReadOnly }: RiskAssessmentQuestionSectionProps) {
  const selectedWealthSource = Form.useWatch("customerSourceOfWealth", form);
  const selectedWealthSourceValues = Array.isArray(selectedWealthSource)
    ? selectedWealthSource
    : selectedWealthSource
      ? [selectedWealthSource]
      : [];
  const hasOtherWealthSource = selectedWealthSourceValues.includes("F");

  return (
    <div className={zuuStyles.page}>
      <CustomCard title="身份核實" className={zuuStyles.sectionCard} bordered={false}>
        <YesNoRadio name="hasVerifiedLatestIdentityDocument" label="是否已核實客戶最新的身份證明文件？" disabled={isReadOnly} />
      </CustomCard>

      <CustomCard title="國家風險" className={zuuStyles.sectionCard} bordered={false}>
        <ThreeLevelRadio name="customerResidenceRiskLevel" label="客戶居住地的風險程度？" disabled={isReadOnly} />
      </CustomCard>

      <CustomCard title="職業資料" className={zuuStyles.sectionCard} bordered={false}>
        <ThreeLevelRadio name="customerIndustryRiskLevel" label="客戶從事行業的風險程度？" disabled={isReadOnly} />
      </CustomCard>

      <CustomCard title="資金和財富來源" className={zuuStyles.sectionCard} bordered={false}>
        <YesNoRadio name="customerFundsFromHongKong" label="客戶的資金是否來自香港？" disabled={isReadOnly} />

        <Form.Item name="customerSourceOfWealth" label="客戶的財富來源？" rules={[{ required: true, message: "請選擇" }]}>
          <Checkbox.Group
            disabled={isReadOnly}
            className={zuuStyles.checkboxGroup}
            onChange={(values) => {
              if (!values.includes("F")) {
                form.setFieldValue("customerSourceOfWealth_F_remark", undefined);
              }
            }}
          >
            <Row gutter={[20, 14]}>
              {wealthSourceOptions.map((option) => (
                <Col span={8} key={option.value}>
                  {option.value === "F" ? (
                    <div className={zuuStyles.otherInlineOption}>
                      <Checkbox value={option.value} className={zuuStyles.inlineRadio}>
                        {option.label}
                      </Checkbox>
                      <Form.Item noStyle name="customerSourceOfWealth_F_remark">
                        <Input
                          placeholder="請輸入"
                          disabled={isReadOnly || !hasOtherWealthSource}
                          className={zuuStyles.inlineInput}
                          onClick={(event) => event.stopPropagation()}
                        />
                      </Form.Item>
                    </div>
                  ) : (
                    <Checkbox value={option.value}>{option.label}</Checkbox>
                  )}
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        </Form.Item>
      </CustomCard>

      <CustomCard title="交易活動" className={zuuStyles.sectionCard} bordered={false}>
        <Form.Item name="initialPremiumPaymentMethod" label="首期保費的繳付方式？" rules={[{ required: true, message: "請選擇" }]}>
          <Radio.Group disabled={isReadOnly} className={zuuStyles.checkboxGroup}>
            <Row gutter={[20, 14]}>
              {transactionMethodOptions.map((option) => (
                <Col span={8} key={option.value}>
                  <Radio value={option.value}>{option.label}</Radio>
                </Col>
              ))}
            </Row>
          </Radio.Group>
        </Form.Item>

        <Form.Item name="renewalPremiumPaymentMethod" label="續期保費的繳付方式（如適用）？" rules={[{ required: true, message: "請選擇" }]}>
          <Radio.Group disabled={isReadOnly} className={zuuStyles.checkboxGroup}>
            <Row gutter={[20, 14]}>
              {transactionMethodOptions.map((option) => (
                <Col span={8} key={`settlement-${option.value}`}>
                  <Radio value={option.value}>{option.label}</Radio>
                </Col>
              ))}
            </Row>
          </Radio.Group>
        </Form.Item>

        <Form.Item name="expectedAnnualTransactionCount" label="預計客戶每年需進行的交易活動次數？" rules={[{ required: true, message: "請選擇" }]}>
          <Radio.Group disabled={isReadOnly} className={zuuStyles.checkboxGroup}>
            <Row gutter={[20, 14]}>
              <Col span={8}><Radio value="A">少於 10 次</Radio></Col>
              <Col span={8}><Radio value="B">10 到 50 次</Radio></Col>
              <Col span={8}><Radio value="C">多於 50 次</Radio></Col>
            </Row>
          </Radio.Group>
        </Form.Item>
      </CustomCard>

      <CustomCard title="產品風險（只適用於人壽保險）" className={zuuStyles.sectionCard} bordered={false}>
        <div className={zuuStyles.infoBox}>
          <div>符合以下說明的人壽保險單：</div>
          <div>∎ 預繳付的每年年繳保費不少於 120,000 USD，或累積共有任何產品應繳付的相同保費；或</div>
          <div>∎ 預繳付的一筆整付保費不少於 400,000 USD，或累積共有產品應繳付的相同保費。</div>
        </div>

        <YesNoRadio name="qualifyingLifeInsurancePolicy" label="符合以下說明的人壽保險單？" disabled={isReadOnly} />
        <YesNoRadio name="hasInsurableInterest" label="客戶與受益人是否有可保利益？" disabled={isReadOnly} />
      </CustomCard>

      <CustomCard title="識別政治人物及恐怖分子" className={zuuStyles.sectionCard} bordered={false}>
        <YesNoRadio name="hasPepOrTerroristConnection" label="客戶是否與政治人物 / 恐怖分子有任何連繫？" disabled={isReadOnly} />
      </CustomCard>
    </div>
  );
}
